/**
 * The Velocity logo, as the brand guide draws it (Brand Guide v2.0, pages 6-14).
 *
 * The paths are the guide's own vector artwork, not a redrawing: `Violations`
 * (p10) forbids recreating, distorting or restroking the logo, so the geometry
 * is lifted verbatim and only uniformly scaled.
 *
 * Two lock-ups, both horizontal - the only orientation the guide permits:
 *
 *   <VelocityMark />  the "V" symbol on its own (p14). It is ALWAYS Velocity
 *                     Green; the guide calls out a blue or restyled "V" as a
 *                     violation, so the fill is hard-coded rather than themed.
 *   <VelocityLogo />  the full logo: green "V" + the "elocity" letter type.
 *                     The letter type follows `currentColor` so it is Velocity
 *                     Dark Blue on light surfaces and white on dark ones, which
 *                     are the two approved two-colour variants (p9).
 *
 * The trademark symbol rides with the full logo. The guide allows dropping it
 * when the logo is too small for it to be legible (p9), which is what
 * `showTrademark={false}` is for - the 20px sidebar lock-up, where it would
 * render as a smudge.
 */

/** Velocity Green. Fixed: the symbol mark may not be recoloured. */
const VELOCITY_GREEN = '#72C16A';

export interface LogoProps {
  /** Rendered height in px. Width follows the logo's fixed aspect ratio. */
  height?: number;
  className?: string;
}

export function VelocityMark({ height = 20, className }: LogoProps) {
  return (
    <svg
      viewBox="0 0 82.22 100"
      height={height}
      width={height * 0.8222}
      role="img"
      aria-label="Velocity"
      className={className}
    >
      <path
        d="M0.0 31.726L40.488 100.0L82.218 0.0L40.291 71.401L0.0 31.726Z"
        fill={VELOCITY_GREEN}
      />
    </svg>
  );
}

export function VelocityLogo({
  height = 24,
  className,
  showTrademark = true,
}: LogoProps & { showTrademark?: boolean }) {
  return (
    <svg
      viewBox="0 0 271.9 100.0"
      height={height}
      width={height * 2.719}
      role="img"
      aria-label="Velocity"
      className={className}
    >
      <path
        d="M0.0 31.726L40.488 100.0L82.218 0.0L40.291 71.401L0.0 31.726Z"
        fill={VELOCITY_GREEN}
      />
      <path
        d="M81.106 71.335L78.228 78.502L63.838 78.502L62.726 82.085C62.53 82.606 62.53 83.127 62.726 83.518C62.988 83.909 63.38 84.104 63.969 84.104L81.433 84.104L78.097 91.792L55.531 91.792C54.419 91.792 53.569 91.401 53.111 90.619C52.588 89.837 52.588 88.86 53.046 87.752L63.119 62.215C63.576 61.107 64.361 60.13 65.473 59.349C66.585 58.567 67.697 58.176 68.809 58.176L91.44 58.176L88.104 65.863L70.641 65.863C70.052 65.863 69.463 66.059 68.94 66.449C68.417 66.84 68.024 67.362 67.763 67.883L66.651 71.466L81.106 71.466L81.106 71.335ZM109.231 91.726L87.123 91.726C86.011 91.726 85.161 91.336 84.703 90.554C84.18 89.772 84.18 88.795 84.638 87.687L96.281 58.046L103.999 58.046L94.384 82.085C94.187 82.606 94.187 83.127 94.384 83.518C94.645 83.909 95.038 84.104 95.626 84.104L112.109 84.104L109.231 91.726ZM142.982 87.687C142.524 88.795 141.739 89.772 140.627 90.554C139.515 91.336 138.403 91.726 137.291 91.726L117.276 91.726C116.165 91.726 115.314 91.336 114.856 90.554C114.333 89.772 114.333 88.795 114.791 87.687L124.864 62.15C125.322 61.042 126.107 60.065 127.219 59.283C128.33 58.502 129.442 58.111 130.554 58.111L150.569 58.111C151.746 58.111 152.531 58.502 153.055 59.283C153.513 60.065 153.578 61.042 153.12 62.15L142.982 87.687M137.815 82.02L143.832 67.687C144.028 67.166 144.028 66.645 143.832 66.254C143.57 65.863 143.178 65.668 142.589 65.668L132.844 65.668C132.255 65.668 131.666 65.863 131.143 66.254C130.62 66.645 130.227 67.166 129.966 67.687L123.948 82.02C123.752 82.541 123.752 83.062 123.948 83.453C124.21 83.844 124.602 84.039 125.191 84.039L134.937 84.039C135.525 84.039 136.114 83.844 136.637 83.453C137.161 83.062 137.553 82.606 137.815 82.02ZM185.039 58.046L181.703 65.733L165.221 65.733C164.632 65.733 164.043 65.928 163.52 66.319C162.997 66.71 162.604 67.231 162.343 67.752L157.306 82.085C157.11 82.606 157.11 83.127 157.306 83.518C157.568 83.909 157.96 84.104 158.549 84.104L175.032 84.104L171.696 91.792L150.111 91.792C148.999 91.792 148.149 91.401 147.691 90.619C147.168 89.837 147.168 88.86 147.626 87.752L157.699 62.215C158.156 61.107 158.941 60.13 160.053 59.349C161.165 58.567 162.277 58.176 163.389 58.176L185.039 58.176L185.039 58.046ZM190.403 58.046L198.579 58.046L185.301 91.792L177.125 91.792L190.403 58.046ZM207.605 91.726L199.429 91.726L209.044 67.687C209.24 67.166 209.24 66.645 209.044 66.254C208.782 65.863 208.39 65.668 207.801 65.668L200.083 65.668L202.896 57.98L230.105 57.98L227.293 65.668L220.098 65.668C219.509 65.668 218.921 65.863 218.397 66.254C217.874 66.645 217.482 67.166 217.22 67.687L207.605 91.726ZM241.552 58.046C241.552 58.046 243.122 67.036 243.252 68.013C243.514 69.707 244.561 69.902 246.523 67.948C247.635 66.84 255.811 58.046 255.811 58.046L265.491 58.046C265.491 58.046 248.354 75.309 247.177 76.417C245.999 77.524 245.018 78.827 244.43 80.326C243.841 81.824 239.393 91.726 239.393 91.726L231.741 91.726C231.741 91.726 235.665 81.889 236.188 80.521C236.777 79.153 237.039 78.111 236.712 76.612C236.385 75.114 232.918 58.046 232.918 58.046L241.552 58.046Z"
        fill="currentColor"
      />
      {showTrademark && (
        <path
          d="M268.762 58.241C268.762 58.371 268.631 58.436 268.565 58.436L267.78 58.436L267.78 60.912C267.78 61.042 267.65 61.173 267.519 61.173C267.388 61.173 267.257 61.042 267.257 60.912L267.257 58.436L266.472 58.436C266.341 58.436 266.276 58.371 266.276 58.241C266.276 58.111 266.407 58.046 266.472 58.046L268.434 58.046C268.631 58.046 268.762 58.111 268.762 58.241ZM271.901 60.977C271.901 61.107 271.77 61.238 271.639 61.238C271.509 61.238 271.378 61.173 271.378 60.977L271.378 58.893L270.658 59.935C270.593 60.0 270.528 60.0 270.462 60.0C270.397 60.0 270.331 60.0 270.266 59.935L269.481 58.893L269.481 60.977C269.481 61.107 269.35 61.238 269.219 61.238C269.089 61.238 268.958 61.107 268.958 60.977L268.958 58.306C268.958 58.176 269.089 58.046 269.219 58.046C269.285 58.046 269.35 58.046 269.416 58.111L270.462 59.414L271.443 58.111C271.509 58.046 271.574 58.046 271.639 58.046C271.77 58.046 271.901 58.176 271.901 58.306L271.901 60.977Z"
          fill="currentColor"
        />
      )}
    </svg>
  );
}
